import { useState, useMemo, useCallback } from "react";
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarExport, GridToolbarQuickFilter, GridToolbarDensitySelector } from "@mui/x-data-grid";
import { Dialog, AppBar, Toolbar, Typography, IconButton, Slide, Box, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

const Transition = (props) => <Slide direction="up" {...props} />;

// Custom toolbar: search + filters + column visibility + density + CSV export + fullscreen toggle
function CustomToolbar({ onFullscreen, isFullscreen, fileName }) {
  return (
    <GridToolbarContainer sx={{ p: 1, gap: 1, flexWrap: "wrap" }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport csvOptions={{ fileName: fileName || "calls-export" }} printOptions={{ disableToolbarButton: true }} />
      <Box sx={{ flex: 1 }} />
      <GridToolbarQuickFilter debounceMs={300} />
      {!isFullscreen && (
        <Tooltip title="View full size">
          <IconButton size="small" onClick={onFullscreen}>
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </GridToolbarContainer>
  );
}

/**
 * Generic, fully-featured MUI DataGrid wrapper for the calls tables.
 *
 * Props:
 * - rows, columns: standard DataGrid props (columns must be GridColDef[])
 * - loading: boolean
 * - rowCount, paginationModel, onPaginationModelChange: server-side pagination
 * - height: grid height when rendered inline (default 420)
 * - title: shown in the fullscreen dialog header
 * - fileName: used for CSV export filename
 * - getRowId: optional custom row id getter (defaults to row.id)
 */
export default function MuiCallsTable({ rows, columns, loading, rowCount, paginationModel, onPaginationModelChange, height = 520, title, fileName, getRowId }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const openFullscreen = useCallback(() => setFullscreen(true), []);
  const closeFullscreen = useCallback(() => setFullscreen(false), []);

  const commonProps = useMemo(
    () => ({
      rows,
      columns,
      loading,
      getRowId: getRowId || ((row) => row.id),
      paginationMode: "server",
      rowCount: rowCount || 0,
      paginationModel,
      onPaginationModelChange,
      pageSizeOptions: [10, 25, 50, 100],
      columnVisibilityModel,
      onColumnVisibilityModelChange: setColumnVisibilityModel,
      disableRowSelectionOnClick: true,
      density: "compact",
      slots: { toolbar: CustomToolbar },
      slotProps: {
        toolbar: { onFullscreen: openFullscreen, isFullscreen: false, fileName },
      },
      sx: {
        border: "none",
        "--DataGrid-containerBackground": "#0EA5E9",
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "#38bdf8", // sky-400
          color: "#fff",
        },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
        "& .MuiDataGrid-cell": { fontSize: "0.75rem" },
        "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "rgba(56,189,248,0.05)" },
        "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(56,189,248,0.15)" },
        "& .MuiDataGrid-columnHeaderTitleContainerContent": { color: "white" },
        "& .MuiDataGrid-row--borderBottom": { backgroundColor: "#0EA5E9" },
      },
    }),
    [rows, columns, loading, rowCount, paginationModel, onPaginationModelChange, columnVisibilityModel, fileName, openFullscreen],
  );

  return (
    <>
      <Box sx={{ width: "100%", height }}>
        <DataGrid {...commonProps} />
      </Box>

      <Dialog fullScreen open={fullscreen} onClose={closeFullscreen} TransitionComponent={Transition} container={document.body} keepMounted={false}>
        <AppBar sx={{ position: "relative", bgcolor: "#38bdf8" }}>
          <Toolbar>
            <Typography sx={{ flex: 1 }} variant="h6">
              {title || "Calls"}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={closeFullscreen}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 2, height: "calc(100vh - 64px)" }}>
          <DataGrid
            {...commonProps}
            slotProps={{
              toolbar: { onFullscreen: closeFullscreen, isFullscreen: true, fileName },
            }}
          />
        </Box>
      </Dialog>
    </>
  );
}
